// Password policy of staff accounts (docs/security.md section 2: NIST SP
// 800-63B — a length floor, a blocklist of common and guessable values, no
// composition rules). Environment-neutral: the setup page checks it while the
// person types, the server checks it again before hashing. Lengths count code
// points, so an emoji counts once, as its owner sees it.
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

export type PasswordPolicyCode = "password_too_short" | "password_too_long";

export type PasswordPolicyResult = { ok: true } | { ok: false; code: PasswordPolicyCode };

export function validatePassword(password: string): PasswordPolicyResult {
  // A code point takes at most two UTF-16 units; skip counting oversized input.
  if (password.length > PASSWORD_MAX_LENGTH * 2) return { ok: false, code: "password_too_long" };
  const length = Array.from(password).length;
  if (length < PASSWORD_MIN_LENGTH) return { ok: false, code: "password_too_short" };
  if (length > PASSWORD_MAX_LENGTH) return { ok: false, code: "password_too_long" };
  return { ok: true };
}

export type PasswordProblem = "too_short" | "too_long" | "common" | "repetitive" | "sequence" | "personal";

// Words that make up most leaked passwords, and names this product and its
// owner are known by. A password whose letters are only one of these (with
// digits and symbols around it, any case) is refused.
const COMMON_WORDS = new Set([
  "password", "passw0rd", "p@ssword", "pass", "parol", "parol1", "qwerty", "qwertyuiop", "asdfgh", "asdfghjkl", "zxcvbn", "zxcvbnm",
  "qazwsx", "1qaz2wsx", "admin", "administrator", "root", "user", "login", "welcome", "letmein", "iloveyou", "monkey", "dragon",
  "master", "football", "baseball", "superman", "batman", "sunshine", "shadow", "princess", "secret", "trustno", "freedom",
  "whatever", "changeme", "default", "test", "testing", "guest", "abc", "abcdef", "abcdefgh", "iron", "ironcrm", "ironsrm",
  "dasha", "titanium", "sindri", "kyiv", "kiev", "ukraine", "ukraina", "slavaukraini", "lviv", "odesa", "kharkiv", "summer",
  "winter", "spring", "autumn", "january", "february", "october", "november", "december", "michael", "nikolay", "mykola", "nick",
  "melnik", "sultan", "bank", "money", "love", "hello", "hellohello", "pussy", "killer", "starwars", "pokemon",
]);

// Character runs people type as "random": keyboard rows, the alphabet, digits.
const SEQUENCES = [
  "abcdefghijklmnopqrstuvwxyz",
  "qwertyuiopasdfghjklzxcvbnm",
  "qwertyuiop",
  "asdfghjkl",
  "zxcvbnm",
  // Digits wrap around: "789012" is a run too.
  "01234567890123456789",
  // The Ukrainian keyboard rows and alphabet, as code points: they are data,
  // not copy, and the architecture check reads any Cyrillic literal as copy.
  String.fromCodePoint(0x439, 0x446, 0x443, 0x43a, 0x435, 0x43d, 0x433, 0x448, 0x449, 0x437, 0x445, 0x457, 0x444, 0x456, 0x432, 0x430, 0x43f, 0x440, 0x43e, 0x43b, 0x434, 0x436, 0x454, 0x44f, 0x447, 0x441, 0x43c, 0x438, 0x442, 0x44c, 0x431, 0x44e),
  String.fromCodePoint(0x430, 0x431, 0x432, 0x433, 0x491, 0x434, 0x435, 0x454, 0x436, 0x437, 0x438, 0x456, 0x457, 0x439, 0x43a, 0x43b, 0x43c, 0x43d, 0x43e, 0x43f, 0x440, 0x441, 0x442, 0x443, 0x444, 0x445, 0x446, 0x447, 0x448, 0x449, 0x44c, 0x44e, 0x44f),
];

function isSequence(text: string): boolean {
  if (text.length < 6) return false;
  for (const run of SEQUENCES) {
    const forward = run.includes(text);
    const backward = run.includes([...text].reverse().join(""));
    if (forward || backward) return true;
  }
  return false;
}

/** The letters of a password with digits and symbols removed, lower-case. */
function core(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}]/gu, "");
}

// Common character swaps ("p@ssw0rd"), undone before the blocklist check.
const LEET: Readonly<Record<string, string>> = { "@": "a", "4": "a", "0": "o", "1": "i", "!": "i", "3": "e", "$": "s", "5": "s", "7": "t", "|": "l" };

function unleet(text: string): string {
  return Array.from(text, (character) => LEET[character] ?? character).join("");
}

/** Personal fragments: the email's local part and domain name, and each word of the name, of four letters or more. */
function personalWords(context: { email?: string | null; displayName?: string | null }): string[] {
  const words: string[] = [];
  const email = (context.email ?? "").toLowerCase();
  const [local = "", domain = ""] = email.split("@");
  words.push(...local.split(/[^\p{L}\p{N}]+/u), domain.split(".")[0] ?? "");
  words.push(...(context.displayName ?? "").toLowerCase().split(/[^\p{L}\p{N}]+/u));
  return words.filter((word) => Array.from(word).length >= 4);
}

/**
 * Why a new password is refused, or null when it may be used: shorter than
 * 12 or longer than 128 characters; a common word with digits or symbols
 * around it; mostly repeated (three distinct characters or fewer, one short
 * block over and over, or one character in half of it); a keyboard, alphabet or
 * digit run; or the person's name or email inside it.
 */
export function passwordProblem(password: string, context: { email?: string | null; displayName?: string | null } = {}): PasswordProblem | null {
  const policy = validatePassword(password);
  if (!policy.ok) return policy.code === "password_too_short" ? "too_short" : "too_long";
  const lower = password.toLowerCase();
  const characters = Array.from(lower);
  if (new Set(characters).size <= 3) return "repetitive";
  // "aaaaaaaaaaaaBc7!": one character makes up half or more of the password.
  const counts = new Map<string, number>();
  for (const character of characters) counts.set(character, (counts.get(character) ?? 0) + 1);
  if (Math.max(...counts.values()) * 2 >= characters.length) return "repetitive";
  // "abcabcabcabc", "passpasspass": one short block repeated.
  for (let size = 1; size <= characters.length / 3; size += 1) {
    const block = characters.slice(0, size).join("");
    if (block.repeat(Math.ceil(characters.length / size)).startsWith(lower)) return "repetitive";
  }

  const letters = core(lower);
  // Digits and symbols around a word are trimmed, swaps undone: "P@ssw0rd2026!" is "password".
  const trimmed = unleet(lower.replace(/^[^\p{L}@$!|]+|[^\p{L}@$!|]+$/gu, ""));
  const candidates = [...new Set([letters, core(trimmed), trimmed.replace(/[^\p{L}]/gu, "")])].filter((candidate) => candidate.length > 0);
  if (candidates.some((candidate) => COMMON_WORDS.has(candidate))) return "common";
  // Digits only: a date, a phone number or a run, never random enough.
  if (/^\d+$/.test(lower)) return "sequence";

  const alphanumeric = lower.replace(/[^\p{L}\p{N}]/gu, "");
  const letterRun = letters.length >= 6 && letters.length * 2 >= alphanumeric.length && isSequence(letters);
  if (isSequence(alphanumeric) || letterRun) return "sequence";
  // A common word that makes up half or more of the letters ("sultan-office-77").
  if (candidates.some((candidate) => [...COMMON_WORDS].some((word) => word.length >= 5 && candidate.includes(word) && word.length * 2 >= candidate.length))) return "common";
  if (personalWords(context).some((word) => lower.includes(word))) return "personal";
  return null;
}
